import { visuallyHidden } from '@mui/utils';
import { createContext, useCallback, useContext, useState } from 'react';
import type { Context } from 'react';

import { useAnnouncementQueue } from './useAnnouncementQueue';
import { LIVE_REGION_CONTEXT_DEFAULT } from './useLiveRegion.shared-mocks';
import type {
  Announcement,
  AnnouncementPriority,
  LiveRegionContextValue,
  LiveRegionProviderProps,
} from './useLiveRegion.types';
import { DEFAULT_ANNOUNCEMENT_PRIORITY } from './useLiveRegions.constants';

export const LiveRegionContext = createContext<LiveRegionContextValue | null>(
  null
);

export function useLiveRegion() {
  const context = useContext(LiveRegionContext);

  if (!context) {
    throw new Error('useLiveRegion must be used within a <LiveRegionProvider>');
  }

  return context;
}

export function LiveRegionProvider({ children }: LiveRegionProviderProps) {
  const [currentAnnouncement, setCurrentAnnouncement] =
    useState<Announcement | null>(null);
  const { addAnnouncement } = useAnnouncementQueue(setCurrentAnnouncement);

  const announce = useCallback(
    (
      message: string,
      priority: AnnouncementPriority = DEFAULT_ANNOUNCEMENT_PRIORITY
    ) => {
      addAnnouncement({ message, priority });
    },
    [addAnnouncement]
  );

  return (
    <LiveRegionContext.Provider value={{ announce }}>
      <div
        aria-live={
          currentAnnouncement?.priority ?? DEFAULT_ANNOUNCEMENT_PRIORITY
        }
        role="status"
        aria-atomic="true"
        style={visuallyHidden}
      >
        {currentAnnouncement?.message}
      </div>
      {children}
    </LiveRegionContext.Provider>
  );
}

export const withLiveRegionProvider = ({
  children,
}: LiveRegionProviderProps) => (
  <LiveRegionProvider>{children}</LiveRegionProvider>
);

export const withLiveRegionContextDecorator = (
  data?: Partial<LiveRegionContextValue>
) => ({
  context: LiveRegionContext as Context<LiveRegionContextValue>,
  contextValue: ({ args }: { args: Partial<LiveRegionContextValue> }) =>
    LIVE_REGION_CONTEXT_DEFAULT({ ...args, ...data }),
});
